import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { VirtualElement } from '@floating-ui/react';
import type { CommandItemProps } from './CommandItem';
import { CommandItem } from './CommandItem';
import type { EmoticonItemProps } from './EmoticonItem';
import { EmoticonItem } from './EmoticonItem';
import type { SuggestionListItemComponentProps } from './SuggestionListItem';
import { SuggestionListItem as DefaultSuggestionListItem } from './SuggestionListItem';
import type { UserItemProps } from './UserItem';
import { UserItem } from './UserItem';
import { useComponentContext } from '../../../context/ComponentContext';
import { useMessageComposerContext } from '../../../context/MessageComposerContext';
import { useStateStore } from '../../../store';
import { getTextareaCaretRect } from '../../../utils/getTextareaCaretRect';
import type { ContextMenuItemComponent, ContextMenuItemProps } from '../../Dialog';
import { ContextMenu } from '../../Dialog';
import { usePopoverPosition } from '../../Dialog/hooks/usePopoverPosition';
import { InfiniteScrollPaginator } from '../../InfiniteScrollPaginator/InfiniteScrollPaginator';
import {
  useMessageComposerCommands,
  useMessageComposerController,
} from '../../MessageComposer/hooks';
import { useTranslationContext } from '../../../context';
import type {
  SearchSourceState,
  TextComposerState,
  TextComposerSuggestion,
} from 'stream-chat';
import {
  CommandsMenuClassName,
  CommandsMenuHeader,
} from '../../MessageComposer/AttachmentSelector/CommandsMenu';

type SuggestionTrigger = '/' | ':' | '@' | string;

export type SuggestionListProps = Partial<{
  suggestionItemComponents: Record<
    SuggestionTrigger,
    React.ComponentType<SuggestionListItemComponentProps>
  >;
  className?: string;
  closeOnClickOutside?: boolean;
  containerClassName?: string;
  focusedItemIndex: number;
  setFocusedItemIndex: (index: number) => void;
}>;

const textComposerStateSelector = ({ selection, suggestions }: TextComposerState) => ({
  selection,
  suggestions,
});

const searchSourceStateSelector = (
  nextValue: SearchSourceState<TextComposerSuggestion>,
): { items: TextComposerSuggestion[] } => ({
  items: nextValue.items ?? [],
});

export const defaultComponents: Record<
  SuggestionTrigger,
  React.ComponentType<SuggestionListItemComponentProps>
> = {
  '/': (props: SuggestionListItemComponentProps) => (
    <CommandItem {...props} entity={props.entity as CommandItemProps['entity']} />
  ),
  ':': (props: SuggestionListItemComponentProps) => (
    <EmoticonItem {...props} entity={props.entity as EmoticonItemProps['entity']} />
  ),
  '@': (props: SuggestionListItemComponentProps) => (
    <UserItem {...props} entity={props.entity as UserItemProps['entity']} />
  ),
} as const;

export const SuggestionList = ({
  className,
  closeOnClickOutside = true,
  containerClassName,
  focusedItemIndex,
  setFocusedItemIndex,
  suggestionItemComponents = defaultComponents,
}: SuggestionListProps) => {
  const { t } = useTranslationContext();
  const {
    AutocompleteSuggestionItem = DefaultSuggestionListItem,
    ContextMenu: ContextMenuComponent = ContextMenu,
  } = useComponentContext();
  const { textareaRef } = useMessageComposerContext();
  const messageComposer = useMessageComposerController();
  const commands = useMessageComposerCommands();
  const { textComposer } = messageComposer;
  const { selection, suggestions } = useStateStore(
    textComposer.state,
    textComposerStateSelector,
  );
  const { items } =
    useStateStore(suggestions?.searchSource.state, searchSourceStateSelector) ?? {};
  const hasEnabledCommandSuggestions = useMemo(
    () =>
      suggestions?.searchSource.type !== 'commands' ||
      commands.some(({ enabled }) => enabled),
    [commands, suggestions?.searchSource.type],
  );

  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const caretRectRef = useRef<DOMRect | null>(null);
  const virtualCaretReference = useMemo<VirtualElement>(
    () => ({
      getBoundingClientRect: () => caretRectRef.current ?? new DOMRect(),
    }),
    [],
  );

  const { refs, strategy, update, x, y } = usePopoverPosition({
    allowFlip: false,
    offset: 8,
    placement: 'top-start',
    // For top placements, the cross-axis is X; we need this to allow flipping the list to the right when it overflows the right edge.
    shiftOptions: { crossAxis: true },
  });

  const component = suggestions?.trigger
    ? suggestionItemComponents[suggestions?.trigger]
    : undefined;

  const contextMenuItems = useMemo<ContextMenuItemComponent[]>(() => {
    if (!component) return [];
    const sortedItems =
      suggestions?.searchSource.type === 'commands'
        ? [...(items ?? [])].sort((a, b) =>
            String((a as { name?: string }).name ?? '').localeCompare(
              String((b as { name?: string }).name ?? ''),
            ),
          )
        : (items ?? []);
    return sortedItems.map((item, i) => {
      const Item: ContextMenuItemComponent = ({ ...props }: ContextMenuItemProps) => (
        <AutocompleteSuggestionItem
          {...props}
          component={component}
          focused={focusedItemIndex === i}
          item={item}
          key={item.id.toString()}
          onMouseEnter={() => setFocusedItemIndex?.(i)}
        />
      );
      return Item;
    });
  }, [
    items,
    component,
    focusedItemIndex,
    setFocusedItemIndex,
    AutocompleteSuggestionItem,
    suggestions?.searchSource.type,
  ]);

  const ItemsWrapper = useCallback(
    ({ children }: React.ComponentProps<'div'>) => (
      <InfiniteScrollPaginator
        loadNextOnScrollToBottom={suggestions?.searchSource.search}
        threshold={100}
      >
        {children}
      </InfiniteScrollPaginator>
    ),
    [suggestions?.searchSource.search],
  );

  useEffect(() => {
    if (!closeOnClickOutside || !suggestions || !container) return;
    const handleClick = (event: MouseEvent) => {
      if (container.contains(event.target as Node)) return;
      textComposer.closeSuggestions();
    };
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [closeOnClickOutside, suggestions, container, textComposer]);

  useEffect(() => {
    refs.setFloating(container);
  }, [container, refs]);

  useLayoutEffect(() => {
    if (!suggestions || !update) return;
    const updatePosition = () => {
      const rect = getTextareaCaretRect(textareaRef.current ?? null, selection?.end);
      if (!rect) {
        caretRectRef.current = null;
        refs.setReference(null);
        return;
      }
      caretRectRef.current = rect;
      virtualCaretReference.contextElement = textareaRef.current ?? undefined;
      refs.setReference(virtualCaretReference);
      update();
    };

    updatePosition();
  }, [
    container,
    items?.length,
    refs,
    selection?.end,
    suggestions,
    textareaRef,
    update,
    virtualCaretReference,
  ]);

  if (!suggestions || !items?.length || !component || !hasEnabledCommandSuggestions)
    return null;

  const suggestionMenuLabel =
    suggestions.searchSource.type === 'commands'
      ? t('aria/Command Suggestions')
      : suggestions.searchSource.type === 'emojis'
        ? t('aria/Emoji Suggestions')
        : suggestions.searchSource.type === 'mentions'
          ? t('aria/User Suggestions')
          : t('aria/Suggestions');

  return (
    <div
      className={clsx('str-chat__suggestion-list-container', containerClassName)}
      ref={setContainer}
      style={{
        left: x ?? 0,
        position: strategy,
        top: y ?? 0,
        visibility: x == null || y == null ? 'hidden' : undefined,
        zIndex: 1000,
      }}
    >
      <ContextMenuComponent
        aria-label={suggestionMenuLabel}
        className={clsx('str-chat__suggestion-list', className)}
        Header={
          suggestions.searchSource.type === 'commands' ? CommandsMenuHeader : undefined
        }
        items={contextMenuItems}
        ItemsWrapper={ItemsWrapper}
        menuClassName={
          suggestions.searchSource.type === 'commands' ? CommandsMenuClassName : undefined
        }
      />
    </div>
  );
};
